import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BarChart3, Users, UserCheck, Briefcase, FileText } from "lucide-react";
import api from "../../services/api";

interface TenantStats {
  users: { total: number; active: number };
  candidates: { total: number };
  jobs: { total: number; open: number };
  applications: { total: number };
}

const TenantAnalyticsTab: React.FC = () => {
  const { t } = useTranslation(["admin", "common"]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<TenantStats | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const result = (await api.tenant.getAnalytics()) as {
        success: boolean;
        stats: TenantStats;
      };
      if (result.success) {
        setStats(result.stats);
      }
    } catch (error) {
      console.error("Error loading analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">{t("analytics.loading")}</div>;
  }

  if (!stats) {
    return (
      <div className="admin-section">
        <div className="section-header">
          <h2>
            <BarChart3 size={16} /> {t("analytics.title")}
          </h2>
        </div>
        <p>{t("analytics.noData")}</p>
      </div>
    );
  }

  const cards = [
    {
      label: t("analytics.cards.totalUsers"),
      value: stats.users.total,
      sub: t("analytics.cards.activeCount", { count: stats.users.active }),
      icon: <Users size={24} />,
      color: "#3b82f6",
    },
    {
      label: t("analytics.cards.candidates"),
      value: stats.candidates.total,
      sub: t("analytics.cards.inDatabase"),
      icon: <UserCheck size={24} />,
      color: "#10b981",
    },
    {
      label: t("analytics.cards.jobs"),
      value: stats.jobs.total,
      sub: t("analytics.cards.openCount", { count: stats.jobs.open }),
      icon: <Briefcase size={24} />,
      color: "#f59e0b",
    },
    {
      label: t("analytics.cards.applications"),
      value: stats.applications.total,
      sub: t("analytics.cards.total"),
      icon: <FileText size={24} />,
      color: "#8b5cf6",
    },
  ];

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>
          <BarChart3 size={16} /> {t("analytics.title")}
        </h2>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "16px",
          marginTop: "16px",
        }}
      >
        {cards.map((card) => (
          <div
            key={card.label}
            style={{
              background: "var(--bg-secondary, #fff)",
              border: "1px solid var(--border-color, #e5e7eb)",
              borderRadius: "8px",
              padding: "20px",
              display: "flex",
              alignItems: "flex-start",
              gap: "16px",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "8px",
                backgroundColor: `${card.color}15`,
                color: card.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {card.icon}
            </div>
            <div>
              <div
                style={{
                  fontSize: "0.85rem",
                  color: "var(--text-secondary, #6b7280)",
                }}
              >
                {card.label}
              </div>
              <div
                style={{
                  fontSize: "1.75rem",
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                {card.value.toLocaleString()}
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "var(--text-tertiary, #9ca3af)",
                }}
              >
                {card.sub}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TenantAnalyticsTab;
